// Funnel management functions
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, getDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

/**
 * Get all funnels
 */
export const getAllFunnels = async (filters = {}) => {
  try {
    const funnelsRef = collection(db, 'funnels');
    let q = query(funnelsRef, orderBy('createdAt', 'desc'));
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting funnels:', error);
    return [];
  }
};

/**
 * Get a single funnel by ID
 */
export const getFunnel = async (funnelId) => {
  try {
    const funnelRef = doc(db, 'funnels', funnelId);
    const snapshot = await getDoc(funnelRef);
    
    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting funnel:', error);
    return null;
  }
};

/**
 * Get funnel by slug (for public pages)
 */
export const getFunnelBySlug = async (slug) => {
  try {
    const funnelsRef = collection(db, 'funnels');
    const q = query(funnelsRef, where('slug', '==', slug), where('status', '==', 'active'));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    return {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    };
  } catch (error) {
    console.error('Error getting funnel by slug:', error);
    return null;
  }
};

/**
 * Create a new funnel
 */
export const createFunnel = async (funnelData) => {
  try {
    const funnelsRef = collection(db, 'funnels');
    
    // Generate slug if not provided
    if (!funnelData.slug) {
      funnelData.slug = funnelData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }
    
    const newFunnel = {
      ...funnelData,
      status: funnelData.status || 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(funnelsRef, newFunnel);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating funnel:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a funnel
 */
export const updateFunnel = async (funnelId, updates) => {
  try {
    const funnelRef = doc(db, 'funnels', funnelId);
    await updateDoc(funnelRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating funnel:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a funnel
 */
export const deleteFunnel = async (funnelId) => {
  try {
    const funnelRef = doc(db, 'funnels', funnelId);
    await deleteDoc(funnelRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting funnel:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Track funnel conversion
 */
export const trackFunnelConversion = async (funnelId, stepId, conversionType, metadata = {}) => {
  try {
    const conversionsRef = collection(db, 'funnelConversions');
    await addDoc(conversionsRef, {
      funnelId,
      stepId,
      conversionType, // 'view', 'opt-in', 'purchase', 'upsell-accept', 'upsell-decline'
      metadata,
      timestamp: serverTimestamp(),
      sessionId: sessionStorage.getItem('sessionId') || `session_${Date.now()}`,
      userId: localStorage.getItem('userId') || null
    });
    
    // Update funnel analytics
    const funnelRef = doc(db, 'funnels', funnelId);
    const funnelSnap = await getDoc(funnelRef);
    if (funnelSnap.exists()) {
      const funnel = funnelSnap.data();
      const analytics = funnel.analytics || {
        views: 0,
        optIns: 0,
        purchases: 0,
        upsellAccepts: 0,
        upsellDeclines: 0,
        revenue: 0
      };
      
      // Increment appropriate counter
      if (conversionType === 'view') analytics.views++;
      else if (conversionType === 'opt-in') analytics.optIns++;
      else if (conversionType === 'purchase') {
        analytics.purchases++;
        analytics.revenue += (metadata.total || 0);
      }
      else if (conversionType === 'upsell-accept') {
        analytics.upsellAccepts++;
        analytics.revenue += (metadata.total || 0);
      }
      else if (conversionType === 'upsell-decline') analytics.upsellDeclines++;
      
      await updateDoc(funnelRef, {
        analytics,
        updatedAt: serverTimestamp()
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error tracking funnel conversion:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get funnel analytics
 */
export const getFunnelAnalytics = async (funnelId, startDate, endDate) => {
  try {
    const conversionsRef = collection(db, 'funnelConversions');
    const q = query(
      conversionsRef,
      where('funnelId', '==', funnelId),
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const conversions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by date range if provided
    let filtered = conversions;
    if (startDate && endDate) {
      filtered = conversions.filter(conv => {
        const timestamp = conv.timestamp?.toDate?.() || new Date(conv.timestamp);
        return timestamp >= startDate && timestamp <= endDate;
      });
    }
    
    // Calculate metrics
    const analytics = {
      views: filtered.filter(c => c.conversionType === 'view').length,
      optIns: filtered.filter(c => c.conversionType === 'opt-in').length,
      purchases: filtered.filter(c => c.conversionType === 'purchase').length,
      upsellAccepts: filtered.filter(c => c.conversionType === 'upsell-accept').length,
      upsellDeclines: filtered.filter(c => c.conversionType === 'upsell-decline').length,
      revenue: filtered
        .filter(c => c.conversionType === 'purchase' || c.conversionType === 'upsell-accept')
        .reduce((sum, c) => sum + (c.metadata?.total || 0), 0),
      optInRate: 0,
      purchaseRate: 0,
      upsellAcceptRate: 0
    };
    
    if (analytics.views > 0) {
      analytics.optInRate = (analytics.optIns / analytics.views) * 100;
    }
    if (analytics.optIns > 0) {
      analytics.purchaseRate = (analytics.purchases / analytics.optIns) * 100;
    }
    if (analytics.purchases > 0) {
      analytics.upsellAcceptRate = (analytics.upsellAccepts / analytics.purchases) * 100;
    }
    
    return analytics;
  } catch (error) {
    console.error('Error getting funnel analytics:', error);
    return null;
  }
};



